import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import checkmarkIcon from '../../assets/images/checkmark.png';
import crossIcon from '../../assets/images/cross.png';

import './styles.css';
import Collapsable from '../../components/Collapsable';
import FirmSection from '../../components/FirmSection';
import HtmlContentBlock from '../../components/HtmlContentBlock';
import POPUP_STYLES from '../../constants/popup-styles';
import LinkParser from '../../helpers/link-parser';
import FormDialog from '../Dialog/components/FormDialog';
import ResponseErrorDialog from '../Dialog/components/ResponseErrorDialog';
import FirmOverviewEditForm from '../Form/components/FirmOverviewEditForm';

const FirmOverview = props => {
  const {
    firm: {
      FirmID,
      Site,
      OfficeLocationName,
      TypeName,
      FirmSizeName,
      About,
      Esg,
      Diversity,
    },
    isEditMode,
    onEditOverview,
  } = props;

  const [siteHostname, siteUrl] = useMemo(() => {
    if (Site) {
      return [
        LinkParser.getHostName(Site) || Site,
        'http://' + Site.replace(/^https?:\/\//, ''),
      ];
    }
    return [];
  }, [Site]);

  let markYes = <img src={checkmarkIcon} alt="checkmark" />;
  let markNo = <img src={crossIcon} alt="cross" />;

  function renderStatIcon(stat) {
    if (stat === 'Yes') {
      return markYes;
    } else if (stat === 'No') {
      return markNo;
    } else {
      return (
        <span>
          {markYes} {markNo}
        </span>
      );
    }
  }

  const editOverview = () => {
    FormDialog({
      title: '',
      modeless: false,
      compact: true,
      popupStyles: { ...POPUP_STYLES.formPopupStyles, maxWidth: '540px' },
      popupInnerStyles: { ...POPUP_STYLES.formInnerStyles, marginTop: '30px' },
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      params: { id: FirmID },
      form: (actions, params) => (
        <FirmOverviewEditForm actions={actions} params={params} />
      ),
    }).then(
      response => onEditOverview(response),
      response => ResponseErrorDialog({ response })
    );
  };

  return (
    <div className="firm-tab-overview">
      <FirmSection
        title="Overview"
        controls={isEditMode && [{ caption: 'Edit', handler: editOverview }]}
      >
        <div className="firm-tab-overview__top">
          <div className="firm-tab-overview__info-item">
            <h3 className="firm-tab-overview__info-label">Website</h3>
            {siteHostname ? (
              <a
                className="firm-tab-overview__info-link"
                target={'_blank'}
                href={siteUrl}
              >
                {siteHostname}
              </a>
            ) : (
              <span className="firm-tab-overview__info-text">---</span>
            )}
          </div>

          <div className="firm-tab-overview__info-item">
            <h3 className="firm-tab-overview__info-label">Company Size</h3>
            <span className="firm-tab-overview__info-text">
              {FirmSizeName ? FirmSizeName : '---'}
            </span>
          </div>

          <div className="firm-tab-overview__info-item">
            <h3 className="firm-tab-overview__info-label">ESG Focus</h3>
            <span className="firm-tab-overview__info-text">
              <span className="firm-tab-overview__icons">
                {renderStatIcon(Esg)}
              </span>
            </span>
          </div>

          <div className="firm-tab-overview__info-item">
            <h3 className="firm-tab-overview__info-label">Headquarters</h3>
            <span className="firm-tab-overview__info-text">
              {OfficeLocationName ? OfficeLocationName : '---'}
            </span>
          </div>

          <div className="firm-tab-overview__info-item">
            <h3 className="firm-tab-overview__info-label">Company Type</h3>
            <span className="firm-tab-overview__info-text">
              {TypeName ? TypeName : '---'}
            </span>
          </div>

          <div className="firm-tab-overview__info-item">
            <h3 className="firm-tab-overview__info-label">Diversity Focus</h3>
            <span className="firm-tab-overview__info-text">
              <span className="firm-tab-overview__icons">
                {renderStatIcon(Diversity)}
              </span>
            </span>
          </div>
        </div>
        <div className="firm-tab-overview__description">
          {About && (
            <Collapsable
              btnHidden={
                About.length < 400 && About.split(/\r\n|\r|\n/).length <= 5
              }
              maskHidden={
                About.length < 400 && About.split(/\r\n|\r|\n/).length <= 5
              }
            >
              <HtmlContentBlock content={About} />
            </Collapsable>
          )}
        </div>
      </FirmSection>
    </div>
  );
};

FirmOverview.propTypes = {
  firm: PropTypes.shape({
    FirmID: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
    Site: PropTypes.string,
    OfficeLocationName: PropTypes.string,
    TypeName: PropTypes.string,
    FirmSizeName: PropTypes.string,
    About: PropTypes.string,
    Esg: PropTypes.oneOf(['Yes', 'No', 'Both']),
    Diversity: PropTypes.oneOf(['Yes', 'No', 'Both']),
  }),
  isEditMode: PropTypes.bool.isRequired,
  onEditOverview: PropTypes.func.isRequired,
};

export default FirmOverview;
